import React, {
  FC,
  ImgHTMLAttributes,
  useCallback,
  useRef,
  useState,
} from "react";
import clsx from "classnames";
import { useStyles } from "./Image.style";
import { Skeleton } from "../Skeleton";

interface IImageProps {
  url: string;
  url2x?: string;
  url3x?: string;
  radius?: number | string;
}

const getUrlWithRatio = (url: string, ratio: "@2x" | "@3x"): string => {
  const arraySplitted = url.split(".");

  const extension = arraySplitted.pop();
  const name = arraySplitted.pop();

  arraySplitted.push(name + ratio);
  arraySplitted.push(extension || "");
  return arraySplitted.join(".");
};

export const ImageComponent: FC<
  IImageProps & ImgHTMLAttributes<HTMLImageElement>
> = ({ alt, url, url2x, url3x, radius, onLoad, ...otherProps }) => {
  const classes = useStyles();

  const ref = useRef<HTMLImageElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      onLoad?.(event);
      setIsLoading(false);
    },
    [],
  );

  const onError = () => {
    if (ref.current) {
      if (ref.current.srcset !== url) {
        ref.current.srcset = url;
      } else {
        ref.current.style.opacity = "0";
      }
    }
  };

  return (
    <>
      {isLoading && (
        <Skeleton
          {...otherProps}
          data-testid="skeleton-image"
          style={{ ...otherProps.style }}
          borderRadius={radius}
          className={clsx(
            classes.root,
            { isHide: !isLoading },
            otherProps.className,
          )}
        />
      )}
      <img
        {...otherProps}
        className={clsx(
          classes.root,
          { isHide: isLoading },
          otherProps.className,
        )}
        srcSet={
          otherProps.srcSet ?? (url2x && url3x)
            ? `${url} 1x, ${url2x} 2x, ${url3x} 3x`
            : url
        }
        style={{ borderRadius: radius }}
        ref={ref}
        src={url}
        alt={alt}
        onError={onError}
        onLoad={handleLoad}
      />
    </>
  );
};

export const Image = React.memo(ImageComponent);
